// Reusable client-side CSV export helpers.
//
// Designed to be shared across listing pages: pass the rows you already have
// (or fetched) plus a column definition, and these helpers handle RFC-4180
// escaping and the browser download. A UTF-8 BOM is prepended so Excel renders
// unicode/accented characters correctly.

// Escape a single CSV cell per RFC 4180: wrap in quotes when the value contains
// a comma, double-quote, or newline, and double any embedded quotes.
const escapeCell = (value) => {
  if (value === null || value === undefined) return '';
  const str = String(value);
  return /[",\n\r]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

/**
 * Build CSV text from rows + column definitions.
 * @param {Array<Object>} rows
 * @param {Array<{header: string, accessor: string | ((row: Object) => any)}>} columns
 * @returns {string} CSV text (CRLF line endings)
 */
export const buildCsv = (rows, columns) => {
  const headerLine = columns.map((c) => escapeCell(c.header)).join(',');
  const dataLines = rows.map((row) =>
    columns
      .map((c) => {
        const raw = typeof c.accessor === 'function' ? c.accessor(row) : row[c.accessor];
        return escapeCell(raw);
      })
      .join(',')
  );
  return [headerLine, ...dataLines].join('\r\n');
};

/**
 * Trigger a browser download of CSV text as a .csv file.
 * @param {string} filename - with or without a .csv suffix
 * @param {string} csvText
 */
export const downloadCsv = (filename, csvText) => {
  const BOM = String.fromCharCode(0xfeff); // helps Excel detect UTF-8
  const blob = new Blob([BOM + csvText], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename.endsWith('.csv') ? filename : `${filename}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Convenience: build CSV from rows/columns and download in one call.
 * @param {string} filename
 * @param {Array<Object>} rows
 * @param {Array<{header: string, accessor: string | ((row: Object) => any)}>} columns
 */
export const exportToCsv = (filename, rows, columns) => {
  downloadCsv(filename, buildCsv(rows, columns));
};
